import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ObjectKind } from "../../../../api/restaurantSchema";
import { ELEMENT_STYLES, OBJECT_KINDS } from "../../../../components/restaurant-schema/elementCatalog";
import { PlusIcon } from "../../../../components/restaurant-schema/schemaIcons";
import ScrollTabs from "../../../../components/ScrollTabs";

// Под този праг натискането е клик (добави в свободно място), над него — влачене.
const DRAG_THRESHOLD_PX = 5;

type PaletteDrag = {
  kind: ObjectKind;
  pointerId: number;
  startX: number;
  startY: number;
  dragging: boolean;
};

/**
 * Единственото място за добавяне (дизайн, т. 6): „+ Маса" отваря формата,
 * обектите се ПЛЪЗГАТ върху схемата (или с клик/тап отиват в свободно място).
 * Влаченето е с Pointer Events + pointer capture, затова работи и с пръст.
 * Обект, пуснат обратно върху самата лента, не се добавя (отказ).
 */
const AddToolbar = ({
  showHint,
  onAddTable,
  onAddObject,
  onDropObject,
}: {
  // „Плъзни обект върху схемата" — само на широко платно, иначе не се събира.
  showHint: boolean;
  onAddTable: () => void;
  onAddObject: (kind: ObjectKind) => void;
  // Пуснат върху екрана — родителят решава дали точката е върху платното.
  onDropObject: (kind: ObjectKind, clientX: number, clientY: number) => void;
}) => {
  const { t } = useTranslation();
  const toolbarRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<PaletteDrag | null>(null);
  const [ghost, setGhost] = useState<{ kind: ObjectKind; x: number; y: number } | null>(null);

  const isOverToolbar = (clientX: number, clientY: number) => {
    const rect = toolbarRef.current?.getBoundingClientRect();
    return Boolean(
      rect && clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom,
    );
  };

  const bindItem = (kind: ObjectKind) => ({
    onPointerDown: (event: React.PointerEvent<HTMLButtonElement>) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = {
        kind,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        dragging: false,
      };
    },
    onPointerMove: (event: React.PointerEvent<HTMLButtonElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      const distance = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
      if (!drag.dragging && distance < DRAG_THRESHOLD_PX) return;
      drag.dragging = true;
      setGhost({ kind, x: event.clientX, y: event.clientY });
    },
    onPointerUp: (event: React.PointerEvent<HTMLButtonElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      dragRef.current = null;
      setGhost(null);
      if (!drag.dragging) {
        onAddObject(kind);
        return;
      }
      if (!isOverToolbar(event.clientX, event.clientY)) onDropObject(kind, event.clientX, event.clientY);
    },
    onPointerCancel: () => {
      dragRef.current = null;
      setGhost(null);
    },
  });

  const GhostIcon = ghost ? ELEMENT_STYLES[ghost.kind].Icon : null;

  return (
    <>
      <div
        ref={toolbarRef}
        className="dk-elevated absolute bottom-3 left-1/2 z-30 flex max-w-[calc(100%-24px)] -translate-x-1/2 rounded-[14px] bg-surface p-2 shadow-[0_1px_2px_rgba(28,25,23,0.08),0_10px_30px_rgba(28,25,23,0.12)] sm:bottom-5"
        onPointerDown={(event) => event.stopPropagation()}
      >
        {/* На тясно платно („Вход", „Сцена", „DJ" не се събират) ScrollTabs дава
            преливане по ръба, стрелки за мишка и скрол с колелцето. */}
        <ScrollTabs activeKey={null} className="gap-1">
          <button
            type="button"
            onClick={onAddTable}
            className="flex h-[52px] shrink-0 cursor-pointer items-center gap-2 rounded-[10px] border-none bg-brand px-[18px] text-[14.5px] font-bold text-white transition-colors duration-150 hover:bg-brand-deep"
          >
            <PlusIcon className="h-[17px] w-[17px]" strokeWidth={2.4} />
            {t("restaurantSchema.kinds.table")}
          </button>
          <span className="mx-1.5 h-[34px] w-px shrink-0 bg-shade-3" />
          {OBJECT_KINDS.map((kind) => {
            const style = ELEMENT_STYLES[kind];
            return (
              <button
                key={kind}
                type="button"
                title={t("restaurantSchema.addBar.dragHint")}
                {...bindItem(kind)}
                className={`flex h-[52px] w-16 shrink-0 cursor-grab touch-none select-none flex-col items-center justify-center gap-1 rounded-[10px] border-none bg-transparent transition-colors duration-150 hover:bg-fill active:cursor-grabbing ${style.iconClass}`}
              >
                <style.Icon className="h-[19px] w-[19px]" />
                <span className="text-[11.5px] font-semibold leading-[1.2] text-ink-2">
                  {t(style.labelKey)}
                </span>
              </button>
            );
          })}
          {showHint && (
            <>
              <span className="mx-1.5 h-[34px] w-px shrink-0 bg-shade-3" />
              <span className="w-[84px] shrink-0 pr-1.5 text-[11.5px] leading-[1.35] text-muted">
                {t("restaurantSchema.addBar.hint")}
              </span>
            </>
          )}
        </ScrollTabs>
      </div>

      {ghost && GhostIcon && (
        <div
          className={`pointer-events-none fixed z-[80] flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[12px] border-[1.5px] shadow-[0_8px_24px_rgba(28,25,23,0.18)] ${ELEMENT_STYLES[ghost.kind].bodyClass}`}
          style={{ left: ghost.x, top: ghost.y }}
        >
          <GhostIcon className="h-5 w-5" />
        </div>
      )}
    </>
  );
};

export default AddToolbar;
